/**
 * AgentChatManager - Manages multiple floating chat windows at the bottom of the screen
 *
 * This component displays chat windows at the bottom-right of the screen.
 * Users can:
 * - Open multiple chat sessions with different agents
 * - Minimize/expand chat windows (minimized windows collapse to header only)
 * - Close chat sessions
 * - Switch between active chats
 */
import React, { useState, useCallback, useMemo } from 'react';
import { useAppTheme } from '../../contexts/ThemeContext';
import AgentChatWindow from './AgentChatWindow.js';
import { useAgentChat } from '../../contexts/AgentChatContext.js';

const getStyles = (theme) => ({
  container: {
    position: 'fixed',
    bottom: 0,
    right: '20px',
    display: 'flex',
    flexDirection: 'row-reverse',
    alignItems: 'flex-end',
    gap: '12px',
    zIndex: 1000,
    pointerEvents: 'none', // Allow clicks through container
  },
  windowWrapper: {
    pointerEvents: 'auto', // Enable clicks on windows
  },
});

const AgentChatManager = () => {
  const { theme } = useAppTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  const {
    getOpenChats,
    toggleMinimize,
    closeChat,
    closePendingChat,
    sendMessageStream,
    switchSession,
    isLoading,
    chatSessions,
    pendingWindows,
  } = useAgentChat();

  // Get all open chat sessions with their window states
  const openChats = getOpenChats();
  const pendingEntries = Array.from(pendingWindows.entries());

  // Handle sending a message to a specific session
  const handleSendMessage = useCallback(
    async (sessionId, message, attachments = []) => {
      switchSession(sessionId);
      await sendMessageStream(message, attachments);
    },
    [switchSession, sendMessageStream],
  );

  // Handle closing a chat
  const handleCloseChat = useCallback(
    async (sessionId) => {
      await closeChat(sessionId);
    },
    [closeChat],
  );

  // If no chats or pending windows are open, don't render anything
  if (openChats.length === 0 && pendingEntries.length === 0) {
    return null;
  }

  return (
    <div style={styles.container}>
      {/* Pending (loading) windows */}
      {pendingEntries.map(([pendingId, pending], index) => (
        <div
          key={pendingId}
          style={{
            ...styles.windowWrapper,
            marginRight: openChats.length > 0 || index > 0 ? '8px' : 0,
          }}
        >
          <AgentChatWindow
            windowState={{
              isMinimized: pending.isMinimized,
              agentName: pending.agentName,
              agentType: pending.agentType,
            }}
            loadingStatus={pending.loadingStatus}
            onClose={() => closePendingChat(pendingId)}
            isLoading={false}
          />
        </div>
      ))}
      {/* Active session windows */}
      {openChats.map((chat, index) => {
        const session = chatSessions.get(chat.id);
        return (
          <div
            key={chat.id}
            style={{
              ...styles.windowWrapper,
              marginRight: index > 0 ? '8px' : 0,
            }}
          >
            <AgentChatWindow
              session={session}
              windowState={chat.windowState}
              onToggleMinimize={() => toggleMinimize(chat.id)}
              onClose={() => handleCloseChat(chat.id)}
              onSendMessage={handleSendMessage}
              isLoading={isLoading}
            />
          </div>
        );
      })}
    </div>
  );
};

export default AgentChatManager;
